import { useState, useEffect } from 'react';
import { quotationsAPI, quotationCatalogAPI } from '../../services/quotationService';

const STATUS_BADGES = {
  borrador: { label: 'Borrador', bg: 'bg-gray-100', text: 'text-gray-600' },
  emitida: { label: 'Emitida', bg: 'bg-blue-100', text: 'text-blue-700' },
  aceptada: { label: 'Aceptada', bg: 'bg-green-100', text: 'text-green-700' },
  rechazada: { label: 'Rechazada', bg: 'bg-red-100', text: 'text-red-700' },
};

export default function QuotationsList({ onNew, onEdit }) {
  const [quotations, setQuotations] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, limit: 20, offset: 0 });
  const [loading, setLoading] = useState(true);
  const [agents, setAgents] = useState([]);
  const [campaigns, setCampaigns] = useState([]);

  // Filters
  const [filterAgent, setFilterAgent] = useState('');
  const [filterCampaign, setFilterCampaign] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');

  useEffect(() => {
    quotationCatalogAPI.getAgents().then(setAgents).catch(console.error);
    quotationCatalogAPI.getCampaigns().then(setCampaigns).catch(console.error);
  }, []);

  const fetchQuotations = async (offset = 0) => {
    setLoading(true);
    try {
      const params = { limit: pagination.limit, offset };
      if (filterAgent) params.agent_id = filterAgent;
      if (filterCampaign) params.campaign_id = filterCampaign;
      if (filterStatus) params.status = filterStatus;
      if (filterDateFrom) params.date_from = filterDateFrom;
      if (filterDateTo) params.date_to = filterDateTo;

      const result = await quotationsAPI.list(params);
      setQuotations(result.data || []);
      setPagination(result.pagination || { total: 0, limit: 20, offset: 0 });
    } catch (err) {
      console.error('Error fetching quotations:', err);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchQuotations(0);
  }, [filterAgent, filterCampaign, filterStatus, filterDateFrom, filterDateTo]);

  const handleDuplicate = async (q) => {
    if (!confirm(`Duplicar cotizacion de ${q.client_name}?`)) return;
    try {
      const original = await quotationsAPI.get(q.id);
      await quotationsAPI.create({
        ...original,
        id: undefined,
        status: 'borrador',
        created_at: undefined,
        updated_at: undefined,
        client_name: original.client_name + ' (copia)',
      });
      fetchQuotations(pagination.offset);
    } catch (err) {
      console.error('Error duplicating:', err);
      alert('Error al duplicar cotizacion');
    }
  };

  const handleDelete = async (q) => {
    if (!confirm(`Eliminar cotizacion de ${q.client_name}? Esta accion no se puede deshacer.`)) return;
    try {
      await quotationsAPI.delete(q.id);
      fetchQuotations(pagination.offset);
    } catch (err) {
      console.error('Error deleting:', err);
      alert('Error al eliminar cotizacion');
    }
  };

  const handleStatusChange = async (q, newStatus) => {
    try {
      await quotationsAPI.update(q.id, { status: newStatus });
      fetchQuotations(pagination.offset);
    } catch (err) {
      console.error('Error updating status:', err);
    }
  };

  const formatPrice = (n) => {
    if (n == null) return '$0';
    return '$' + n.toLocaleString('es-CL');
  };

  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('es-CL');
  };

  const totalPages = Math.ceil(pagination.total / pagination.limit);
  const currentPage = Math.floor(pagination.offset / pagination.limit) + 1;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Cotizaciones</h1>
        <button
          onClick={onNew}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium"
        >
          + Nueva Cotizacion
        </button>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-5 gap-3 mb-4">
        <select
          value={filterAgent}
          onChange={(e) => setFilterAgent(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
        >
          <option value="">Todos los asesores</option>
          {agents.map(a => (
            <option key={a.agent_identity_id} value={a.agent_identity_id}>{a.name}</option>
          ))}
        </select>
        <select
          value={filterCampaign}
          onChange={(e) => setFilterCampaign(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
        >
          <option value="">Todas las campanas</option>
          {campaigns.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
        >
          <option value="">Todos los estados</option>
          <option value="borrador">Borrador</option>
          <option value="emitida">Emitida</option>
          <option value="aceptada">Aceptada</option>
          <option value="rechazada">Rechazada</option>
        </select>
        <input
          type="date"
          value={filterDateFrom}
          onChange={(e) => setFilterDateFrom(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          placeholder="Desde"
        />
        <input
          type="date"
          value={filterDateTo}
          onChange={(e) => setFilterDateTo(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          placeholder="Hasta"
        />
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-600">Fecha</th>
              <th className="px-4 py-3 text-left font-medium text-gray-600">Cliente</th>
              <th className="px-4 py-3 text-left font-medium text-gray-600">Asesor</th>
              <th className="px-4 py-3 text-left font-medium text-gray-600">Campana</th>
              <th className="px-4 py-3 text-right font-medium text-gray-600">Total c/IVA</th>
              <th className="px-4 py-3 text-center font-medium text-gray-600">Estado</th>
              <th className="px-4 py-3 text-center font-medium text-gray-600">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-gray-400">Cargando...</td>
              </tr>
            ) : quotations.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-gray-400">
                  No hay cotizaciones
                </td>
              </tr>
            ) : quotations.map((q, idx) => {
              const badge = STATUS_BADGES[q.status] || STATUS_BADGES.borrador;
              return (
                <tr key={q.id} className={`border-t border-gray-100 hover:bg-gray-50 ${idx % 2 === 1 ? 'bg-gray-50/50' : ''}`}>
                  <td className="px-4 py-3 text-gray-600">{formatDate(q.created_at)}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-800">{q.client_name || '-'}</div>
                    {q.prospect_phone && (
                      <div className="text-xs text-gray-400">{q.prospect_phone}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-600">{q.agent_name || '-'}</td>
                  <td className="px-4 py-3 text-gray-600 text-xs">{q.campaign_name || '-'}</td>
                  <td className="px-4 py-3 text-right font-medium">{formatPrice(q.total_con_iva)}</td>
                  <td className="px-4 py-3 text-center">
                    <select
                      value={q.status}
                      onChange={(e) => handleStatusChange(q, e.target.value)}
                      className={`px-2 py-1 rounded-full text-xs font-medium border-0 cursor-pointer ${badge.bg} ${badge.text}`}
                    >
                      <option value="borrador">Borrador</option>
                      <option value="emitida">Emitida</option>
                      <option value="aceptada">Aceptada</option>
                      <option value="rechazada">Rechazada</option>
                    </select>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex gap-1 justify-center">
                      <button
                        onClick={() => onEdit(q.id)}
                        className="px-2 py-1 text-xs bg-blue-50 text-blue-600 rounded hover:bg-blue-100"
                        title="Editar"
                      >
                        Editar
                      </button>
                      <button
                        onClick={() => handleDuplicate(q)}
                        className="px-2 py-1 text-xs bg-gray-50 text-gray-600 rounded hover:bg-gray-100"
                        title="Duplicar"
                      >
                        Duplicar
                      </button>
                      <button
                        onClick={() => handleDelete(q)}
                        className="px-2 py-1 text-xs bg-red-50 text-red-600 rounded hover:bg-red-100"
                        title="Eliminar"
                      >
                        Eliminar
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <div className="text-sm text-gray-500">
            {pagination.total} cotizaciones total
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => fetchQuotations(Math.max(0, pagination.offset - pagination.limit))}
              disabled={currentPage <= 1}
              className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Anterior
            </button>
            <span className="px-3 py-1 text-sm text-gray-600">
              Pagina {currentPage} de {totalPages}
            </span>
            <button
              onClick={() => fetchQuotations(pagination.offset + pagination.limit)}
              disabled={currentPage >= totalPages}
              className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Siguiente
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
